import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  ATTENDANCE_LOG_TYPE,
  AttendanceLog,
  BREAK_TIMER_STATUS,
  BreakTimer,
  Class,
  Student,
  Teacher,
} from "../models";
import { getDayBounds } from "../utils/class-session-utils";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class BreakTimerController {
  /**
   * Start a break (Student/Teacher)
   * @route POST /api/v1/break/start
   * @access Private (Student, Teacher)
   */
  async startBreak(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, breakType, location, remarks } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      if (userRole !== "student" && userRole !== "teacher") {
        res.status(403).json({
          success: false,
          message: "Only students and teachers can start breaks",
        } as APIResponse);
        return;
      }

      // Check if there's an active break
      const activeBreak = await BreakTimer.findActiveBreak(
        mongoose.Types.ObjectId.createFromHexString(userId),
        classId
          ? mongoose.Types.ObjectId.createFromHexString(classId)
          : undefined,
      );

      if (activeBreak) {
        res.status(400).json({
          success: false,
          message: "You already have an active break. Please end it first.",
          data: {
            activeBreak: {
              id: activeBreak.id,
              startedAt: activeBreak.startedAt,
              breakType: activeBreak.breakType,
              durationMinutes: activeBreak.getDurationMinutes(),
            },
          },
        } as APIResponse);
        return;
      }

      // Validate class if provided (required for students)
      let classItem = null;
      let semesterId: mongoose.Types.ObjectId | undefined;
      if (classId) {
        classItem = await Class.findOne({
          _id: classId,
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (!classItem) {
          res.status(404).json({
            success: false,
            message: "Class not found",
          } as APIResponse);
          return;
        }

        semesterId = classItem.semesterId;

        // For students, verify enrollment
        if (userRole === "student") {
          const student = await Student.findOne({
            userId: new mongoose.Types.ObjectId(userId),
            schoolId: new mongoose.Types.ObjectId(schoolId),
          });

          if (!student) {
            res.status(404).json({
              success: false,
              message: "Student profile not found",
            } as APIResponse);
            return;
          }

          if (
            !classItem.studentIds?.some(
              id => id.toString() === student._id.toString(),
            )
          ) {
            res.status(403).json({
              success: false,
              message: "Student not enrolled in this class",
            } as APIResponse);
            return;
          }
        }

        // For teachers, verify assignment
        if (userRole === "teacher") {
          const teacher = await Teacher.findOne({
            userId: new mongoose.Types.ObjectId(userId),
            schoolId: new mongoose.Types.ObjectId(schoolId),
          });

          if (!teacher) {
            res.status(404).json({
              success: false,
              message: "Teacher profile not found",
            } as APIResponse);
            return;
          }

          if (
            !classItem.teacherIds?.some(
              id => id.toString() === teacher._id.toString(),
            )
          ) {
            res.status(403).json({
              success: false,
              message: "Teacher not assigned to this class",
            } as APIResponse);
            return;
          }
        }
      } else if (userRole === "student") {
        res.status(400).json({
          success: false,
          message: "Class ID is required for student breaks",
        } as APIResponse);
        return;
      }

      // Create break timer
      const breakTimer = new BreakTimer({
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
        userRole: userRole,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        classId: classId
          ? mongoose.Types.ObjectId.createFromHexString(classId)
          : undefined,
        semesterId,
        breakType: breakType || "general",
        startedAt: new Date(),
        location,
        remarks,
        status: BREAK_TIMER_STATUS.ACTIVE,
      });

      await breakTimer.save();

      logger.info(`Break started: ${breakTimer.id}`, {
        breakTimerId: breakTimer.id,
        userId,
        userRole,
        classId,
        breakType: breakTimer.breakType,
      });

      res.status(201).json({
        success: true,
        message: "Break started successfully",
        data: {
          breakTimer: {
            id: breakTimer.id,
            breakType: breakTimer.breakType,
            startedAt: breakTimer.startedAt,
            location: breakTimer.location,
            class: classItem
              ? {
                  id: classItem.id,
                  className: classItem.className,
                  classCode: classItem.classCode,
                }
              : null,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Start break error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to start break",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * End/Return from break (Student/Teacher)
   * @route POST /api/v1/break/end
   * @access Private (Student, Teacher)
   */
  async endBreak(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { breakId, remarks } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      // Find active break
      let activeBreak: any = null;

      if (breakId) {
        if (!mongoose.Types.ObjectId.isValid(breakId)) {
          res.status(400).json({
            success: false,
            message: "Invalid break ID",
          } as APIResponse);
          return;
        }

        activeBreak = await BreakTimer.findOne({
          _id: breakId,
          userId: new mongoose.Types.ObjectId(userId),
          status: BREAK_TIMER_STATUS.ACTIVE,
        });
      } else {
        // Find latest active break
        activeBreak = await BreakTimer.findActiveBreak(
          new mongoose.Types.ObjectId(userId),
        );
      }

      if (!activeBreak) {
        res.status(404).json({
          success: false,
          message: "No active break found",
        } as APIResponse);
        return;
      }

      // Verify ownership
      if (activeBreak.userId.toString() !== userId) {
        res.status(403).json({
          success: false,
          message: "You don't have permission to end this break",
        } as APIResponse);
        return;
      }

      // Update remarks if provided
      if (remarks) {
        activeBreak.remarks = remarks;
      }

      // End break
      await activeBreak.endBreak();

      const duration = activeBreak.getDurationMinutes();

      logger.info(`Break ended: ${activeBreak.id}`, {
        breakTimerId: activeBreak.id,
        userId,
        duration,
      });

      res.status(200).json({
        success: true,
        message: "Break ended successfully",
        data: {
          breakTimer: {
            id: activeBreak.id,
            breakType: activeBreak.breakType,
            startedAt: activeBreak.startedAt,
            endedAt: activeBreak.endedAt,
            durationMinutes: duration,
            location: activeBreak.location,
            remarks: activeBreak.remarks,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("End break error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to end break",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get active break (Student/Teacher)
   * @route GET /api/v1/break/active
   * @access Private (Student, Teacher)
   */
  async getActiveBreak(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId } = req.query;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      const activeBreak = await BreakTimer.findActiveBreak(
        new mongoose.Types.ObjectId(userId),
        classId ? new mongoose.Types.ObjectId(classId as string) : undefined,
      );

      if (!activeBreak) {
        res.status(200).json({
          success: true,
          message: "No active break found",
          data: {
            activeBreak: null,
          },
        } as APIResponse);
        return;
      }

      const breakDoc = activeBreak as any;
      const duration = activeBreak.getDurationMinutes();

      res.status(200).json({
        success: true,
        message: "Active break retrieved successfully",
        data: {
          activeBreak: {
            id: activeBreak.id,
            breakType: activeBreak.breakType,
            startedAt: activeBreak.startedAt,
            location: activeBreak.location,
            remarks: activeBreak.remarks,
            durationMinutes: duration,
            elapsedSeconds: Math.floor(
              (Date.now() - activeBreak.startedAt.getTime()) / 1000,
            ),
            class: breakDoc.class
              ? {
                  id: breakDoc.class.id || breakDoc.class._id,
                  className: breakDoc.class.className,
                  classCode: breakDoc.class.classCode,
                }
              : null,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get active break error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve active break",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get break history (Student/Teacher)
   * @route GET /api/v1/break/history
   * @access Private (Student, Teacher)
   */
  async getBreakHistory(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, startDate, endDate, page = 1, limit = 20 } = req.query;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (startDate || endDate) {
        filterQuery.startedAt = {};
        if (startDate) {
          filterQuery.startedAt.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.startedAt.$lte = new Date(endDate as string);
        }
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const breaks = await BreakTimer.find({ ...filterQuery })
        .populate("class", "className classCode courseName")
        .sort({ startedAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await BreakTimer.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Break history retrieved successfully",
        data: {
          breaks: breaks.map(br => {
            const brDoc = br as any;
            return {
              id: br.id,
              breakType: br.breakType,
              startedAt: br.startedAt,
              endedAt: br.endedAt,
              durationMinutes: br.durationMinutes || br.getDurationMinutes(),
              status: br.status,
              location: br.location,
              remarks: br.remarks,
              class: brDoc.class
                ? {
                    id: brDoc.class.id || brDoc.class._id,
                    className: brDoc.class.className,
                    classCode: brDoc.class.classCode,
                    courseName: brDoc.class.courseName,
                  }
                : null,
            };
          }),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get break history error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve break history",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Format date as ISO 8601 string (e.g. 2025-03-10T11:15:00.000Z).
   */
  private toISODateString(d: Date): string {
    return new Date(d).toISOString();
  }

  /**
   * Student break status for a class (checked in/out, active break, breaks list).
   * Returns null if validation fails (caller should have sent error response).
   */
  private async getStudentClassBreakStatusData(
    userId: string,
    classId: string,
  ): Promise<{
    classId: string;
    isCheckedIn: boolean;
    isCheckedOut: boolean;
    classIn: string | null;
    classOut: string | null;
    activeBreak: InstanceType<typeof BreakTimer> | null;
    breaks: { breakStartTime: string; breakEndTime: string | null }[];
  } | null> {
    const student = await Student.findOne({
      userId: new mongoose.Types.ObjectId(userId),
    }).lean();
    if (!student) return null;

    const classObjectId = new mongoose.Types.ObjectId(classId);
    const classItem = await Class.findById(classObjectId).lean();
    if (!classItem) return null;

    const isEnrolled = classItem.studentIds?.some(
      (id: mongoose.Types.ObjectId) => id.toString() === student._id.toString(),
    );
    if (!isEnrolled) return null;

    const { start: dayStart, end: dayEnd } = getDayBounds(new Date());
    const userObjectId = new mongoose.Types.ObjectId(userId);

    const checkInToday = await AttendanceLog.findOne({
      userId: userObjectId,
      classId: classObjectId,
      userRole: "student",
      logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
      timestamp: { $gte: dayStart, $lte: dayEnd },
    });
    const checkOutToday = await AttendanceLog.findOne({
      userId: userObjectId,
      classId: classObjectId,
      userRole: "student",
      logType: ATTENDANCE_LOG_TYPE.CHECK_OUT,
      timestamp: { $gte: dayStart, $lte: dayEnd },
    });
    const isCheckedIn = !!checkInToday;
    const isCheckedOut = !!checkOutToday;
    const classIn = checkInToday?.timestamp
      ? this.toISODateString(checkInToday.timestamp)
      : null;
    const classOut = checkOutToday?.timestamp
      ? this.toISODateString(checkOutToday.timestamp)
      : null;

    const activeBreak = await BreakTimer.findOne({
      userId: userObjectId,
      classId: classObjectId,
      status: BREAK_TIMER_STATUS.ACTIVE,
    });

    const breaksToday = await BreakTimer.find({
      userId: userObjectId,
      classId: classObjectId,
      startedAt: { $gte: dayStart, $lte: dayEnd },
    })
      .sort({ startedAt: 1 })
      .lean();

    const breaks = breaksToday.map(
      (b: { startedAt: Date; endedAt?: Date | null }) => ({
        breakStartTime: this.toISODateString(b.startedAt),
        breakEndTime: b.endedAt ? this.toISODateString(b.endedAt) : null,
      }),
    );

    return {
      classId,
      isCheckedIn,
      isCheckedOut,
      classIn,
      classOut,
      activeBreak,
      breaks,
    };
  }

  /**
   * GET /api/v1/student/classes/:classId/breaks
   * Get break status for the logged-in student in a class (can start/finish, list of breaks, isOnBreak).
   */
  async getStudentClassBreaks(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }
      if (!classId || !mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          classId: classId ?? "",
          classIn: null,
          classOut: null,
          isOnBreak: false,
          breaks: [],
          message: "Invalid class ID",
        });
        return;
      }

      const data = await this.getStudentClassBreakStatusData(userId, classId);
      if (!data) {
        res.status(404).json({
          success: false,
          classId,
          classIn: null,
          classOut: null,
          isOnBreak: false,
          breaks: [],
          message: "Class not found or you are not enrolled in this class.",
        });
        return;
      }

      const isOnBreak = !!data.activeBreak;
      let success = true;
      let message: string | null = null;
      if (!data.isCheckedIn) {
        success = false;
        message =
          "Cannot start break because you are not checked in to the class.";
      } else if (data.isCheckedOut) {
        success = false;
        message =
          "Cannot start or end break because you have already checked out.";
      }

      res.status(200).json({
        success,
        classId: data.classId,
        classIn: data.classIn,
        classOut: data.classOut,
        isOnBreak,
        breaks: data.breaks,
        message,
      });
    } catch (error: unknown) {
      logger.error("Get student class breaks error:", error);
      res.status(500).json({
        success: false,
        classId: (req.params as { classId?: string }).classId ?? "",
        classIn: null,
        classOut: null,
        isOnBreak: false,
        breaks: [],
        message: "Internal server error",
      });
    }
  }

  /**
   * POST /api/v1/student/classes/:classId/breaks/start
   * Start a break. Enforces: checked in, not checked out, only one active break.
   */
  async startStudentClassBreak(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }
      if (!classId || !mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          classId: classId ?? "",
          isOnBreak: false,
          breaks: [],
          message: "Invalid class ID",
        });
        return;
      }

      const data = await this.getStudentClassBreakStatusData(userId, classId);
      if (!data) {
        res.status(404).json({
          success: false,
          classId,
          isOnBreak: false,
          breaks: [],
          message: "Class not found or you are not enrolled in this class.",
        });
        return;
      }

      if (!data.isCheckedIn) {
        res.status(200).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message:
            "Cannot start break because you are not checked in to the class.",
        });
        return;
      }
      if (data.isCheckedOut) {
        res.status(200).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message:
            "Cannot start or end break because you have already checked out.",
        });
        return;
      }
      if (data.activeBreak) {
        res.status(200).json({
          success: false,
          classId: data.classId,
          isOnBreak: true,
          breaks: data.breaks,
          message:
            "Cannot start a new break because you are already on a break.",
        });
        return;
      }

      let schoolId = req.user?.schoolId;
      const classItem = await Class.findById(classId)
        .select("schoolId semesterId")
        .lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message: "Class not found.",
        });
        return;
      }
      if (!schoolId && classItem.schoolId != null) {
        schoolId = String(classItem.schoolId);
      }
      if (!schoolId) {
        res.status(400).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message: "School context is required.",
        });
        return;
      }

      const semesterId = classItem.semesterId;

      const breakTimer = new BreakTimer({
        userId: new mongoose.Types.ObjectId(userId),
        userRole: "student",
        schoolId: new mongoose.Types.ObjectId(schoolId),
        classId: new mongoose.Types.ObjectId(classId),
        semesterId,
        breakType: "restroom",
        startedAt: new Date(),
        status: BREAK_TIMER_STATUS.ACTIVE,
      });
      await breakTimer.save();

      const updated = await this.getStudentClassBreakStatusData(
        userId,
        classId,
      );
      const updatedData = updated ?? {
        classId,
        breaks: [
          ...data.breaks,
          {
            breakStartTime: this.toISODateString(breakTimer.startedAt),
            breakEndTime: null,
          },
        ],
      };

      res.status(200).json({
        success: true,
        classId: updatedData.classId,
        isOnBreak: true,
        breaks: updatedData.breaks,
        message: null,
      });
    } catch (error: unknown) {
      logger.error("Start student class break error:", error);
      res.status(500).json({
        success: false,
        classId: (req.params as { classId?: string }).classId ?? "",
        isOnBreak: false,
        breaks: [],
        message: "Internal server error",
      });
    }
  }

  /**
   * POST /api/v1/student/classes/:classId/breaks/end
   * End the current break. Enforces: checked in, not checked out, currently on break.
   */
  async endStudentClassBreak(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }
      if (!classId || !mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          classId: classId ?? "",
          isOnBreak: false,
          breaks: [],
          message: "Invalid class ID",
        });
        return;
      }

      const data = await this.getStudentClassBreakStatusData(userId, classId);
      if (!data) {
        res.status(404).json({
          success: false,
          classId,
          isOnBreak: false,
          breaks: [],
          message: "Class not found or you are not enrolled in this class.",
        });
        return;
      }

      if (!data.isCheckedIn) {
        res.status(200).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message:
            "Cannot start break because you are not checked in to the class.",
        });
        return;
      }
      if (data.isCheckedOut) {
        res.status(200).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message:
            "Cannot start or end break because you have already checked out.",
        });
        return;
      }
      if (!data.activeBreak) {
        res.status(200).json({
          success: false,
          classId: data.classId,
          isOnBreak: false,
          breaks: data.breaks,
          message: "Cannot end break because you are not currently on a break.",
        });
        return;
      }

      await data.activeBreak.endBreak();

      const updated = await this.getStudentClassBreakStatusData(
        userId,
        classId,
      );
      const updatedData = updated ?? { classId, breaks: data.breaks };

      res.status(200).json({
        success: true,
        classId: updatedData.classId,
        isOnBreak: false,
        breaks: updatedData.breaks,
        message: null,
      });
    } catch (error: unknown) {
      logger.error("End student class break error:", error);
      res.status(500).json({
        success: false,
        classId: (req.params as { classId?: string }).classId ?? "",
        isOnBreak: false,
        breaks: [],
        message: "Internal server error",
      });
    }
  }

  /**
   * Get student breaks for teacher's class
   * @route GET /api/v1/teacher/classes/:classId/breaks
   * @access Private (Teacher)
   */
  async getClassBreaks(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId } = req.params;
      const { startDate, endDate, page = 1, limit = 20 } = req.query;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
        } as APIResponse);
        return;
      }

      // Verify teacher teaches this class
      const classItem = await Class.findOne({
        _id: classId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        teacherIds: teacher._id,
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or teacher not assigned to this class",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        classId: new mongoose.Types.ObjectId(classId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        userRole: "student", // Only show student breaks
      };

      if (startDate || endDate) {
        filterQuery.startedAt = {};
        if (startDate) {
          filterQuery.startedAt.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.startedAt.$lte = new Date(endDate as string);
        }
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const breaks = await BreakTimer.find({ ...filterQuery })
        .populate("user", "email")
        .sort({ startedAt: -1 })
        .skip(skip)
        .limit(limitNum);

      // Get student info for each break
      const breaksWithStudentInfo = await Promise.all(
        breaks.map(async br => {
          const brDoc = br as any;
          let studentInfo = null;

          if (br.userId) {
            const student = await Student.findOne({
              userId: br.userId,
              schoolId: new mongoose.Types.ObjectId(schoolId),
            }).select("fullName studentId profilePicture");

            if (student) {
              studentInfo = {
                id: student.id,
                fullName: student.fullName,
                studentId: student.studentId,
                profilePicture: student.profilePicture,
              };
            }
          }

          return {
            ...brDoc.toObject(),
            student: studentInfo,
          };
        }),
      );

      const total = await BreakTimer.countDocuments(filterQuery);

      // Get active breaks count
      const activeBreaksCount = await BreakTimer.countDocuments({
        ...filterQuery,
        status: BREAK_TIMER_STATUS.ACTIVE,
      });

      res.status(200).json({
        success: true,
        message: "Class breaks retrieved successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
          },
          activeBreaksCount,
          breaks: breaksWithStudentInfo.map(br => {
            return {
              id: br.id,
              student: br.student,
              breakType: br.breakType,
              startedAt: br.startedAt,
              endedAt: br.endedAt,
              durationMinutes: br.durationMinutes || br.getDurationMinutes(),
              status: br.status,
              location: br.location,
              remarks: br.remarks,
              isActive: br.isActive(),
            };
          }),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get class breaks error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve class breaks",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

export const breakTimerController = new BreakTimerController();
